/**
 * Bulk Condition Reassignment Modal
 *
 * Two-step flow for bulk reassigning conditions to a different team member:
 * 1. Select target assignee from loan associates dropdown
 * 2. Select conditions to reassign (filtered to exclude already assigned)
 * 3. Confirm and push changes to Encompass
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  User,
  RefreshCw,
  ChevronDown,
  Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { EncompassCondition } from '@/types/approval-letter';
import type { LoanAssociate, ConditionOwner } from '@/types/conditions-portal';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';
import { isLockRelatedError } from './bulkReassignUtils';

const log = createComponentLogger('BulkReassignModal', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Internal/system roles that are not valid condition owners.
const EXCLUDED_ROLE_IDS = new Set([
  '0' // File Starter - internal system role for loan creation
]);

interface BulkReassignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conditions: EncompassCondition[];
  token?: string; // Required for external (token-based) access
  onSuccess: () => void;
  // Internal mode props (JWT-based access)
  isInternal?: boolean;
  loanId?: string;
  jwt?: string;
  // File lock status (optional to avoid breaking existing usages/mocks)
  isFileLocked?: boolean;
  fileLockedByName?: string | null;
}

interface ReassignResult {
  success: boolean;
  message?: string;
  reassignedCount?: number;
  newOwnerName?: string;
  error?: string;
  queued?: boolean;
  jobId?: string;
  lockedByName?: string | null;
}

type Step = 'loading' | 'select-assignee' | 'select-conditions' | 'submitting' | 'success' | 'queued' | 'error';

export const BulkReassignModal = ({
  open,
  onOpenChange,
  conditions,
  token,
  onSuccess,
  isInternal = false,
  loanId,
  isFileLocked = false,
  fileLockedByName = null
}: BulkReassignModalProps) => {
  const [step, setStep] = useState<Step>('loading');
  const [associates, setAssociates] = useState<LoanAssociate[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<LoanAssociate | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [conditionNotes, setConditionNotes] = useState<Map<string, string>>(new Map());
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ReassignResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmReassignOpen, setConfirmReassignOpen] = useState(false);
  const [ccEmailInput, setCcEmailInput] = useState('');

  // Filter to assignable roles and deduplicate by roleId
  // Each role should appear once (same person with multiple roles shows each role)
  const uniqueAssociates = useMemo(() => {
    const seen = new Map<string, LoanAssociate>();
    for (const associate of associates) {
      // Skip excluded roles (File Starter, etc.)
      if (EXCLUDED_ROLE_IDS.has(associate.roleId)) {
        continue;
      }
      // Deduplicate by roleId
      if (!seen.has(associate.roleId)) {
        seen.set(associate.roleId, associate);
      }
    }
    return Array.from(seen.values());
  }, [associates]);

  /**
   * Get fresh JWT for internal API calls
   */
  const getFreshJwt = useCallback(async (): Promise<string | null> => {
    if (!isInternal) {
      return null;
    }
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData.session?.access_token || null;
  }, [isInternal]);

  /**
   * Fetch loan associates from API
   */
  const fetchAssociates = useCallback(async () => {
    try {
      log.info('Fetching loan associates', { isInternal });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY
      };

      // Add JWT auth header for internal mode (get fresh token)
      if (isInternal) {
        const freshJwt = await getFreshJwt();
        if (!freshJwt) {
          throw new Error('Session expired. Please refresh the page.');
        }
        headers['Authorization'] = `Bearer ${freshJwt}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers,
        body: JSON.stringify(
          isInternal
            ? { action: 'get-associates-internal', loanId }
            : { action: 'get-associates', token }
        )
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch loan associates');
      }

      log.info('Fetched loan associates', { count: data.associates?.length });
      setAssociates(data.associates || []);
      setStep('select-assignee');
    } catch (error) {
      log.error('Failed to fetch associates', { error });
      setLoadError(error instanceof Error ? error.message : 'Failed to load team members');
      setStep('error');
    }
  }, [getFreshJwt, isInternal, loanId, token]);

  // Fetch loan associates when modal opens.
  useEffect(() => {
    if (open) {
      setStep('loading');
      setSelectedAssignee(null);
      setSelectedIds(new Set());
      setConditionNotes(new Map());
      setOpenNotes(new Set());
      setResult(null);
      setLoadError(null);
      void fetchAssociates();
    }
  }, [fetchAssociates, open]);

  // Get available conditions for selected assignee (exclude already assigned)
  // Condition owners use roleId (e.g., '5') and roleName (e.g., 'Loan Processor')
  // NOT username (e.g., 'mleyva') or person name (e.g., 'Mary Leyva')
  const availableConditions = selectedAssignee
    ? conditions.filter(c => {
        const currentOwnerRoleId = c.owner?.entityId; // roleId like '5'
        const currentOwnerRoleName = c.owner?.entityName?.toLowerCase(); // 'Loan Processor'
        const targetRoleId = selectedAssignee.roleId; // roleId like '5'
        const targetRoleName = selectedAssignee.roleName?.toLowerCase(); // 'Loan Processor'

        // Exclude if current owner matches target by roleId or roleName
        return currentOwnerRoleId !== targetRoleId && currentOwnerRoleName !== targetRoleName;
      })
    : [];

  // Get selected conditions
  const selectedConditions = conditions.filter((c) => {
    const conditionId = c.externalId || c.id || c.internalId;
    return conditionId && selectedIds.has(conditionId);
  });

  /**
   * Toggle condition selection
   */
  const toggleCondition = (conditionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };

  /**
   * Select/deselect all available conditions
   */
  const toggleAll = () => {
    if (selectedIds.size === availableConditions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(
        availableConditions
          .map((c) => c.externalId || c.id || c.internalId)
          .filter(Boolean) as string[]
      ));
    }
  };

  /**
   * Handle assignee selection change
   * Uses roleId as the unique key since the same person can have multiple roles
   */
  const handleAssigneeChange = (roleId: string) => {
    const assignee = uniqueAssociates.find(a => a.roleId === roleId);
    setSelectedAssignee(assignee || null);
    setSelectedIds(new Set()); // Clear selection when assignee changes
    setConditionNotes(new Map()); // Clear notes when assignee changes
    setOpenNotes(new Set()); // Clear open states when assignee changes
  };

  /**
   * Toggle note section open/closed for a condition
   */
  const toggleNoteOpen = (conditionId: string) => {
    setOpenNotes(prev => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };

  /**
   * Update note for a condition (max 250 chars)
   */
  const updateNote = (conditionId: string, note: string) => {
    if (note.length <= 250) {
      setConditionNotes(prev => {
        const next = new Map(prev);
        if (note.trim()) {
          next.set(conditionId, note);
        } else {
          next.delete(conditionId);
        }
        return next;
      });
    }
  };

  /**
   * Go to condition selection step
   */
  const handleNext = () => {
    if (selectedAssignee) {
      setStep('select-conditions');
    }
  };

  /**
   * Go back to assignee selection
   */
  const handleBack = () => {
    setStep('select-assignee');
    setSelectedIds(new Set());
    setConfirmReassignOpen(false);
  };

  /**
   * Submit bulk reassignment to API
   */
  const handleSubmit = async () => {
    if (selectedIds.size === 0 || !selectedAssignee) {
      return;
    }

    try {
      setStep('submitting');

      const conditionIds = Array.from(selectedIds);
      // Encompass conditions use Role-based ownership, not User
      // entityId = roleId (e.g., '5' for Loan Processor)
      // entityType = 'Role'
      const newOwner: ConditionOwner = {
        entityId: selectedAssignee.roleId,
        entityName: selectedAssignee.roleName,
        entityType: 'Role'
      };

      log.info('Submitting bulk reassignment', {
        count: conditionIds.length,
        newOwner: selectedAssignee.name
      });

      // Canonical payload: one conditions[] carrying everything the server/worker needs
      // (id, note, and display fields for the assignee email). No separate conditionDetails.
      const canonicalConditions = selectedConditions.map(c => {
        const condId = (c.externalId || c.id || c.internalId)!;
        return {
          conditionId: condId,
          note: conditionNotes.get(condId)?.trim() || undefined,
          title: c.title || 'Untitled Condition',
          // Full condition text so the assignee email can show the actual instruction,
          // not just the short Encompass title.
          description: c.externalDescription?.trim() || c.internalDescription?.trim() || undefined,
          category: c.category || '',
          priorTo: c.priorTo || ''
        };
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY
      };

      // Add JWT auth header for internal mode (get fresh token)
      if (isInternal) {
        const freshJwt = await getFreshJwt();
        if (!freshJwt) {
          throw new Error('Session expired. Please refresh the page.');
        }
        headers['Authorization'] = `Bearer ${freshJwt}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: isInternal ? 'bulk-reassign-internal' : 'bulk-reassign',
          ...(isInternal ? { loanId } : { token }),
          conditions: canonicalConditions,
          newOwner,
          // Notification recipients
          assigneeEmail: selectedAssignee.email,
          assigneeName: selectedAssignee.name,
          assigneeRole: selectedAssignee.roleName,
          portalUrl: window.location.href,
          // Optional manual CC recipients
          ccEmails: ccEmailInput.trim()
            ? ccEmailInput.split(',').map(e => e.trim()).filter(e => e.includes('@'))
            : []
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to reassign conditions');
      }

      // Server returns {status: 'queued'} when the loan was locked at submit time;
      // the write will apply automatically on unlock via the condition-reassignment worker.
      if (data.status === 'queued') {
        log.info('Bulk reassignment queued', {
          jobId: data.jobId,
          reassignedCount: data.reassignedCount,
          newOwner: data.newOwner?.entityName
        });

        setResult({
          success: true,
          queued: true,
          jobId: data.jobId,
          message: data.message,
          reassignedCount: data.reassignedCount,
          newOwnerName: data.newOwner?.entityName,
          lockedByName: fileLockedByName
        });
        setStep('queued');
        return;
      }

      log.info('Bulk reassignment successful', {
        reassignedCount: data.reassignedCount,
        newOwner: data.newOwner?.entityName
      });

      setResult({
        success: true,
        message: data.message,
        reassignedCount: data.reassignedCount,
        newOwnerName: data.newOwner?.entityName
      });

      setStep('success');
      // Note: onSuccess() is called when user clicks "Done" to avoid resetting modal state
    } catch (error) {
      log.error('Bulk reassignment failed', { error, lockedBy: fileLockedByName || 'unknown' });

      let errorMessage: string;
      if (isLockRelatedError(error)) {
        const lockedBy = fileLockedByName || 'another user';
        errorMessage = `Reassignment failed — the loan file is locked by ${lockedBy}. Please try again after they close the file in Encompass.`;
      } else {
        errorMessage = error instanceof Error ? error.message : 'Failed to reassign conditions';
      }

      setResult({
        success: false,
        error: errorMessage
      });
      setStep('error');
    }
  };

  /**
   * Reset and close modal
   */
  const handleClose = () => {
    setStep('loading');
    setAssociates([]);
    setSelectedAssignee(null);
    setSelectedIds(new Set());
    setResult(null);
    setLoadError(null);
    setConfirmReassignOpen(false);
    setCcEmailInput('');
    onOpenChange(false);
  };

  /**
   * Handle success completion - refresh data then close
   */
  const handleSuccessDone = () => {
    onSuccess(); // Refresh conditions data
    handleClose();
  };

  /**
   * Get role display text
   */
  const getRoleDisplay = (associate: LoanAssociate) => {
    if (associate.roleName) {
      return associate.roleName;
    }
    // Map roleId to common names
    switch (associate.roleId) {
      case '1': return 'Loan Officer';
      case '2': return 'Processor';
      case '3': return 'Underwriter';
      default: return 'Team Member';
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="!w-[50vw] !max-w-none p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#183C2E]" />
            <SheetTitle>
              {step === 'loading' && 'Loading...'}
              {step === 'select-assignee' && 'Reassign to Team Member'}
              {step === 'select-conditions' && 'Select Conditions'}
              {step === 'submitting' && 'Reassigning...'}
              {step === 'success' && 'Conditions Reassigned'}
              {step === 'queued' && 'Reassignment Queued'}
              {step === 'error' && 'Error'}
            </SheetTitle>
          </div>
          <SheetDescription>
            {step === 'loading' && 'Loading team members...'}
            {step === 'select-assignee' && 'Select who to reassign conditions to.'}
            {step === 'select-conditions' && `Select conditions to reassign to ${selectedAssignee?.name || 'selected team member'}.`}
            {step === 'submitting' && 'Updating conditions in Encompass...'}
            {step === 'success' && 'Conditions have been reassigned successfully.'}
            {step === 'queued' && 'Your reassignment has been queued and will apply when the loan is unlocked.'}
            {step === 'error' && 'There was a problem reassigning conditions.'}
          </SheetDescription>
        </SheetHeader>

        {/* File Lock Warning */}
        {isFileLocked && (step === 'select-assignee' || step === 'select-conditions') && (
          <div className="mx-6 mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>{fileLockedByName || 'Someone'}</strong> has this loan open in Encompass. Your reassignment will be queued and apply automatically when they close the file.
              </p>
            </div>
          </div>
        )}

        {/* STEP: LOADING */}
        {step === 'loading' && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#183C2E]" />
              <p className="text-sm text-muted-foreground">Loading team members...</p>
            </div>
          </div>
        )}

        {/* STEP: SELECT ASSIGNEE */}
        {step === 'select-assignee' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-6">
                {/* Assignee Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">
                    Step 1: Select who to assign conditions to
                  </label>
                  <Select
                    value={selectedAssignee?.roleId || ''}
                    onValueChange={handleAssigneeChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueAssociates.map((associate) => (
                        <SelectItem key={associate.roleId} value={associate.roleId}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{associate.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({getRoleDisplay(associate)})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {uniqueAssociates.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No team members found for this loan.
                    </p>
                  )}
                </div>

                {/* Selected Assignee Details */}
                {selectedAssignee && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <label className="text-sm font-medium">Selected Team Member</label>
                      <div className="p-4 rounded-lg border bg-[#183C2E]/5 border-[#183C2E]/20">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-[#183C2E] flex items-center justify-center text-white font-medium">
                            {selectedAssignee.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{selectedAssignee.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {getRoleDisplay(selectedAssignee)}
                              {selectedAssignee.email && ` • ${selectedAssignee.email}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <strong>{availableConditions.length}</strong> condition{availableConditions.length !== 1 ? 's' : ''} can be reassigned to {selectedAssignee.name}.
                          {conditions.length - availableConditions.length > 0 && (
                            <span className="text-blue-600">
                              {' '}({conditions.length - availableConditions.length} already assigned)
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Optional CC recipients for notification email */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">CC on notification email <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <Input
                          type="text"
                          placeholder="email1@example.com, email2@example.com"
                          value={ccEmailInput}
                          onChange={e => setCcEmailInput(e.target.value)}
                          className="text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          Processor, LO, and configured recipients are auto-CC'd by the system. Add extra emails here, comma-separated.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={!selectedAssignee || availableConditions.length === 0}
                className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP: SELECT CONDITIONS */}
        {step === 'select-conditions' && selectedAssignee && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-6">
                {/* Assignee Summary */}
                <div className="p-3 rounded-lg bg-[#183C2E]/5 border border-[#183C2E]/20">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[#183C2E]" />
                    <span className="text-sm">
                      <strong>Reassigning to:</strong> {selectedAssignee.name} ({getRoleDisplay(selectedAssignee)})
                    </span>
                  </div>
                </div>

                {/* Conditions Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Step 2: Select conditions to reassign ({selectedIds.size} of {availableConditions.length})
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleAll}
                      className="text-xs h-7"
                    >
                      {selectedIds.size === availableConditions.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {availableConditions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      All conditions are already assigned to {selectedAssignee.name}.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {availableConditions.map((condition) => {
                        const conditionId = condition.externalId || condition.id || condition.internalId;
                        const isSelected = conditionId ? selectedIds.has(conditionId) : false;
                        const currentOwner = condition.owner?.entityName || 'Unassigned';

                        return (
                          <div
                            key={conditionId}
                            className={cn(
                              'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors',
                              isSelected
                                ? 'bg-[#183C2E]/5 border-2 border-[#183C2E]/30'
                                : 'hover:bg-muted border border-border'
                            )}
                            onClick={() => conditionId && toggleCondition(conditionId)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => conditionId && toggleCondition(conditionId)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0 space-y-2">
                              <p className="font-medium leading-tight">
                                {condition.title || 'Untitled Condition'}
                              </p>
                              {condition.externalDescription && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {condition.externalDescription}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={cn(
                                    'text-xs font-medium px-2 py-0.5 rounded',
                                    condition.priorTo === 'Docs'
                                      ? 'bg-blue-100 text-blue-700'
                                      : condition.priorTo === 'Funding'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-purple-100 text-purple-700'
                                  )}
                                >
                                  {condition.priorTo === 'Docs'
                                    ? 'PTD'
                                    : condition.priorTo === 'Funding'
                                      ? 'PTF'
                                      : 'AC'}
                                </span>
                                {condition.category && (
                                  <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                    {condition.category}
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  Currently: {currentOwner}
                                </span>
                              </div>

                              {/* Collapsible Note Ribbon - only shows when selected */}
                              {isSelected && conditionId && (
                                <Collapsible
                                  open={openNotes.has(conditionId)}
                                  onOpenChange={() => toggleNoteOpen(conditionId)}
                                >
                                  <div className="border-t border-border/50 mt-3 pt-2">
                                    <CollapsibleTrigger
                                      className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span className="flex items-center gap-1.5">
                                        <ChevronDown className={cn(
                                          'h-4 w-4 transition-transform duration-200',
                                          openNotes.has(conditionId) && 'rotate-180'
                                        )} />
                                        {conditionNotes.get(conditionId) ? 'Edit Note' : 'Add Note'}
                                      </span>
                                      {conditionNotes.get(conditionId) && (
                                        <span className="text-xs">
                                          {conditionNotes.get(conditionId)?.length}/250
                                        </span>
                                      )}
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                                      <Textarea
                                        placeholder="Add notes for the assignee..."
                                        value={conditionNotes.get(conditionId) || ''}
                                        onChange={(e) => updateNote(conditionId, e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="mt-2 min-h-[60px] max-h-[100px] text-sm resize-none"
                                        maxLength={250}
                                      />
                                      <div className="text-xs text-muted-foreground text-right mt-1">
                                        {conditionNotes.get(conditionId)?.length || 0}/250
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setConfirmReassignOpen(true)}
                disabled={selectedIds.size === 0}
                className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reassign {selectedIds.size} Condition{selectedIds.size !== 1 ? 's' : ''}
              </Button>
              <AlertDialog open={confirmReassignOpen} onOpenChange={setConfirmReassignOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm Reassignment</AlertDialogTitle>
                    <AlertDialogDescription>
                      Please ensure the Encompass file is unoccupied before proceeding.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
 setConfirmReassignOpen(false); handleSubmit(); 
}}>
                      Continue Reassignment
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {/* STEP: SUBMITTING */}
        {step === 'submitting' && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#183C2E]" />
              <p className="text-sm text-muted-foreground">Updating conditions in Encompass...</p>
            </div>
          </div>
        )}

        {/* STEP: SUCCESS */}
        {step === 'success' && result?.success && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">
                      Conditions Reassigned Successfully
                    </p>
                    <p className="text-sm text-green-700">
                      {result.reassignedCount} condition{result.reassignedCount !== 1 ? 's' : ''} reassigned
                      {result.newOwnerName && ` to ${result.newOwnerName}`}
                    </p>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    The conditions have been updated in Encompass. The page will refresh to show the updated assignments.
                  </p>
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background">
              <Button onClick={handleSuccessDone} className="w-full">Done</Button>
            </div>
          </div>
        )}

        {/* STEP: QUEUED */}
        {step === 'queued' && result?.success && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <Lock className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-medium text-amber-900">
                      Reassignment queued
                    </p>
                    <p className="text-sm text-amber-800">
                      It will apply automatically when the loan is unlocked.
                    </p>
                  </div>
                </div>

                <div className="p-4 border rounded-lg space-y-2">
                  <p className="text-sm text-muted-foreground">
                    <strong>{result.lockedByName || 'Another user'}</strong> currently has this loan open in Encompass.
                    {result.reassignedCount ? ` Your reassignment of ${result.reassignedCount} condition${result.reassignedCount !== 1 ? 's' : ''}` : ' Your reassignment'}
                    {result.newOwnerName ? ` to ${result.newOwnerName}` : ''} has been saved and will be applied automatically once they close the file.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    No further action is needed.
                  </p>
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background">
              <Button onClick={handleSuccessDone} className="w-full">Done</Button>
            </div>
          </div>
        )}

        {/* STEP: ERROR */}
        {step === 'error' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">
                      {loadError ? 'Failed to Load Team Members' : 'Failed to Reassign Conditions'}
                    </p>
                    <p className="text-sm text-red-700">{loadError || result?.error}</p>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={loadError ? fetchAssociates : handleBack}
                className="flex-1"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default BulkReassignModal;
